import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { trackByRef } from 'src/app/lego-utils';
import { combineLatest, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ItemStructure, PartDesignItemStructure } from 'src/app/models/domain';
import {
  DesignService,
  ElementDesignStructureService,
  MouldCavityService,
  PartDesignService,
} from 'src/app/state';
import { MouldDesignService } from 'src/app/state/mould-design/mould-design.service';

@Component({
  selector: 'em-teamcenter',
  templateUrl: './teamcenter.component.html',
  standalone: false,
})
export class TeamcenterComponent implements OnInit, OnDestroy {
  @Output() public readonly setActiveTab = new EventEmitter<string>();

  public readonly trackByRef = trackByRef;

  public designLoading$ = this.designService.loading$;

  public mcIsLoading$ = this.mouldCavityService.loading$;
  public mcError$ = this.mouldCavityService.error$;
  public mouldCavityTabLoading$ = combineLatest([this.designLoading$, this.mcIsLoading$]).pipe(
    map(([designLoading, mouldCavityLoading]) => designLoading || mouldCavityLoading)
  );

  public mdIsLoading$ = this.mouldDesignService.loading$;
  public mdError$ = this.mouldCavityService.error$;
  public mouldDesignTabLoading$ = combineLatest([this.designLoading$, this.mdIsLoading$]).pipe(
    map(([designLoading, mouldDesignLoading]) => designLoading || mouldDesignLoading)
  );

  public pdError$ = this.partDesignService.error$;
  public pdIsLoading$ = this.partDesignService.loading$;
  public partDesignTabLoading$ = combineLatest([this.designLoading$, this.pdIsLoading$]).pipe(
    map(([designLoading, partDesignLoading]) => designLoading || partDesignLoading)
  );

  public edError$ = this.elementDesignRevisionStructureService.error$;
  public edIsLoading$ = this.elementDesignRevisionStructureService.loading$;
  public elementDesignTabLoading$ = combineLatest([this.designLoading$, this.edIsLoading$]).pipe(
    map(([designLoading, elementDesignLoading]) => designLoading || elementDesignLoading)
  );

  public relatedSpecifications$ = this.partDesignService.combinedItemRevisionDatasets$;

  public elementDesignStructure: ItemStructure | undefined;
  public mouldCavityStructure: ItemStructure | undefined;
  public mouldDesignStructure: ItemStructure | undefined;
  public partDesignStructure: PartDesignItemStructure | undefined;

  public teamcenterTabTypes = ['Element Design', 'Mould Cavity', 'Mould Design', 'Part Design'];

  private readonly subscriptions = new Subscription();

  public constructor(
    private readonly designService: DesignService,
    private readonly partDesignService: PartDesignService,
    private readonly elementDesignRevisionStructureService: ElementDesignStructureService,
    private readonly mouldCavityService: MouldCavityService,
    private readonly mouldDesignService: MouldDesignService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.add(
      this.designService.designId$
        .pipe(switchMap((designId) => this.partDesignService.loadById(designId)))
        .subscribe()
    );

    this.subscriptions.add(
      this.elementDesignRevisionStructureService.elementDesignStructure$.subscribe((structure) => {
        this.elementDesignStructure = structure;
      })
    );

    this.subscriptions.add(
      this.elementDesignRevisionStructureService.elementDesignStructure$.subscribe(
        (elementDesignStructure) => (this.elementDesignStructure = elementDesignStructure)
      )
    );

    this.subscriptions.add(
      this.mouldCavityService.itemStructure$.subscribe(
        (mouldCavityStructure) => (this.mouldCavityStructure = mouldCavityStructure)
      )
    );

    this.subscriptions.add(
      this.mouldDesignService.itemStructure$.subscribe(
        (mouldDesignStructure) => (this.mouldDesignStructure = mouldDesignStructure)
      )
    );

    this.subscriptions.add(
      this.partDesignService.partDesignStructure$.subscribe(
        (partDesignStructure) => (this.partDesignStructure = partDesignStructure)
      )
    );

    this.subscriptions.add(this.mouldCavityService.error$.subscribe());
  }

  public adobeTrackingSubTabChange(index: number): void {
    this.setActiveTab.emit(this.teamcenterTabTypes[index]);
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
